import React from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Typography,
  useTheme,
} from '@mui/material'
import LocalHospitalIcon from '@mui/icons-material/LocalHospital'
import PersonSearchIcon from '@mui/icons-material/PersonSearch'
import BlockIcon from '@mui/icons-material/Block'
import HandshakeIcon from '@mui/icons-material/Handshake'
import Footer from 'components/Layout/Footer'
import { useHandleChangeRoute } from 'data/hooks'

const VALUES = [
  {
    icon: PersonSearchIcon,
    title: 'Built for Candidates',
    description:
      'Create a profile, showcase your credentials, and browse positions that match your specialty, experience, and location preferences. Take control of your career search.',
  },
  {
    icon: LocalHospitalIcon,
    title: 'Built for Employers',
    description:
      'Access a comprehensive database of qualified healthcare professionals. Search by specialty, experience, and availability to find the right fit for your organization.',
  },
  {
    icon: BlockIcon,
    title: 'No Third-Party Recruiters',
    description:
      'Every connection on MyStethi is direct. Candidates talk to employers, employers talk to candidates. No middlemen inflating costs or cluttering your inbox.',
  },
  {
    icon: HandshakeIcon,
    title: 'Direct Connections',
    description:
      'We cut out the noise so both sides can focus on what matters: finding the right match. Communicate directly through the platform without barriers.',
  },
]

const About = () => {
  const theme = useTheme()
  const handleChangeRoute = useHandleChangeRoute()

  return (
    <Box>
      <Box
        sx={{
          backgroundColor: theme.palette.background.dark,
          py: { xs: 8, md: 12 },
        }}
      >
        <Container maxWidth='md'>
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant='h2'
              sx={{
                fontWeight: 700,
                mb: 3,
                fontSize: { xs: '2rem', md: '3rem' },
                color: theme.palette.primary.light,
              }}
            >
              About MyStethi
            </Typography>
            <Typography
              variant='h6'
              sx={{
                fontWeight: 400,
                color: 'rgba(255,255,255,0.85)',
                maxWidth: 640,
                mx: 'auto',
                lineHeight: 1.7,
              }}
            >
              MyStethi connects healthcare professionals directly with hospitals and health systems,
              cutting out the middlemen and making the hiring process faster, simpler, and more transparent for everyone.
            </Typography>
          </Box>
        </Container>
      </Box>

      <Container maxWidth='lg' sx={{ py: { xs: 6, md: 10 } }}>
        <Typography
          variant='h4'
          align='center'
          fontWeight={700}
          sx={{ mb: 2 }}
        >
          How We're Different
        </Typography>
        <Typography
          variant='body1'
          align='center'
          color='text.secondary'
          sx={{ mb: 6, maxWidth: 600, mx: 'auto' }}
        >
          Traditional healthcare recruitment is slow, expensive, and full of intermediaries.
          We built MyStethi to change that.
        </Typography>
        <Grid container spacing={4}>
          {VALUES.map(({ icon: Icon, title, description }) => (
            <Grid item xs={12} sm={6} md={3} key={title}>
              <Card
                variant='outlined'
                sx={{
                  height: '100%',
                  borderRadius: '12px',
                  p: 1,
                }}
              >
                <CardContent>
                  <Box sx={{ color: theme.palette.primary.main, mb: 2 }}>
                    <Icon sx={{ fontSize: 40 }} />
                  </Box>
                  <Typography variant='h6' fontWeight={700} gutterBottom>
                    {title}
                  </Typography>
                  <Typography variant='body2' color='text.secondary' sx={{ lineHeight: 1.7 }}>
                    {description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      <Box sx={{ backgroundColor: theme.palette.grey[50], py: { xs: 6, md: 10 } }}>
        <Container maxWidth='md'>
          <Grid container spacing={6} alignItems='center'>
            <Grid item xs={12} md={6}>
              <Typography variant='h4' fontWeight={700} sx={{ mb: 3, color: theme.palette.grey[900] }}>
                Our Mission
              </Typography>
              <Typography variant='body1' sx={{ lineHeight: 1.8, color: theme.palette.grey[700], mb: 2 }}>
                Healthcare staffing shouldn't cost a fortune or take months. MyStethi was created by medical professionals
                who experienced the broken system firsthand and decided to fix it.
              </Typography>
              <Typography variant='body1' sx={{ lineHeight: 1.8, color: theme.palette.grey[700] }}>
                Our platform gives candidates visibility into real opportunities and gives employers access to qualified
                professionals who are actively looking. No outdated job boards, no resume black holes, no recruiter fees.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  backgroundColor: 'white',
                  borderRadius: 3,
                  p: 4,
                  boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                  borderLeft: `4px solid ${theme.palette.primary.main}`,
                }}
              >
                <Typography variant='h6' fontWeight={700} sx={{ mb: 2, color: theme.palette.primary.dark }}>
                  What We Offer
                </Typography>
                {[
                  'Physician, NP, PA, and CRNA job listings',
                  'Direct messaging between candidates and employers',
                  'AI-powered matching and smart alerts',
                  'Salary data and cost-of-living tools',
                  'Hospital directory with in-house recruiter contacts',
                  'Relocation assistance through partner services',
                ].map(item => (
                  <Box key={item} sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5 }}>
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        backgroundColor: theme.palette.primary.main,
                        mt: 1,
                        mr: 1.5,
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant='body2' sx={{ color: theme.palette.grey[700] }}>
                      {item}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box sx={{ py: { xs: 6, md: 10 }, textAlign: 'center' }}>
        <Container maxWidth='sm'>
          <Typography variant='h4' fontWeight={700} gutterBottom>
            Ready to get started?
          </Typography>
          <Typography variant='body1' color='text.secondary' sx={{ mb: 4 }}>
            Whether you're a clinician looking for your next role or an employer searching for top talent,
            MyStethi is built for you.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant='contained'
              size='large'
              onClick={() => handleChangeRoute('/register/physicians')()}
              sx={{
                fontWeight: 700,
                px: 4,
                py: 1.5,
                fontSize: '1rem',
                textTransform: 'none',
                borderRadius: '8px',
              }}
            >
              Create an Account
            </Button>
            <Button
              variant='outlined'
              size='large'
              onClick={() => handleChangeRoute('/contact')()}
              sx={{
                fontWeight: 700,
                px: 4,
                py: 1.5,
                fontSize: '1rem',
                textTransform: 'none',
                borderRadius: '8px',
              }}
            >
              Contact Us
            </Button>
          </Box>
        </Container>
      </Box>
      <Footer />
    </Box>
  )
}

export default About
